/** SA-441: the colour maths behind `--nr-widget-on-primary` and the admin's
 *  WCAG AA warning.
 *
 *  Pure by contract — no store, no DOM — so it is testable as plain functions
 *  and the React admin page can import it without dragging the widget's state
 *  behind it (the `sources.ts` / `hostSite.ts` rule in CLAUDE.md).
 *
 *  `parseColor` understands the formats an author can actually type into the
 *  Branding tab or store through the API. A format it does not understand
 *  returns null and the caller leaves the derived token unset, so the
 *  stylesheet default stands — never a guessed black-or-white, which would be
 *  wrong half the time.
 */

export type Rgb = { r: number; g: number; b: number }

/** The CSS named colours an author might plausibly type. Not the full 148:
 *  this is a convenience, and anything absent simply falls through to null,
 *  which is a safe answer. */
const NAMED: Record<string, string> = {
  black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000',
  blue: '#0000ff', yellow: '#ffff00', orange: '#ffa500', purple: '#800080',
  gray: '#808080', grey: '#808080', silver: '#c0c0c0', maroon: '#800000',
  olive: '#808000', lime: '#00ff00', aqua: '#00ffff', cyan: '#00ffff',
  teal: '#008080', navy: '#000080', fuchsia: '#ff00ff', magenta: '#ff00ff',
  pink: '#ffc0cb', brown: '#a52a2a', beige: '#f5f5dc', ivory: '#fffff0',
  gold: '#ffd700', indigo: '#4b0082', violet: '#ee82ee', tan: '#d2b48c',
  salmon: '#fa8072', coral: '#ff7f50', crimson: '#dc143c', khaki: '#f0e68c',
  lavender: '#e6e6fa', plum: '#dda0dd', turquoise: '#40e0d0',
  rebeccapurple: '#663399', transparent: '#00000000',
}

const HEX = /^#([0-9a-f]{3,8})$/i
const RGB_FN = /^rgba?\(\s*([0-9.]+)\s*[, ]\s*([0-9.]+)\s*[, ]\s*([0-9.]+)\s*(?:[,/]\s*[0-9.%]+\s*)?\)$/i
// SA-441 fix round 2 (AC2): the backend's `_HSL` regex
// (`^hsla?\([^()]*\)$`) accepts an hsl()/hsla() colour and this module is
// what derives --nr-widget-on-primary from whatever an admin typed — a
// primary the write lane accepts but this function cannot read left
// on-primary unset, defaulting to white-on-pale-pink. Content only, no outer
// parens (those are peeled by HSL_FN below).
const HSL_FN = /^hsla?\(([^)]*)\)$/i
const HUE = /^(-?[0-9]*\.?[0-9]+)(deg)?$/i
const PERCENT = /^(-?[0-9]*\.?[0-9]+)%$/

function parseHue(raw: string): number | null {
  const m = HUE.exec(raw.trim())
  if (!m) return null
  const n = Number(m[1])
  if (!Number.isFinite(n)) return null
  // Out-of-range hues wrap: -30deg and 690deg are both 330deg.
  return ((n % 360) + 360) % 360
}

function parsePercent(raw: string): number | null {
  const m = PERCENT.exec(raw.trim())
  if (!m) return null
  const n = Number(m[1])
  if (!Number.isFinite(n)) return null
  // Saturation/lightness clamp rather than reject — CSS itself clamps them.
  return Math.max(0, Math.min(100, n))
}

/** Standard HSL → RGB (hue in degrees already wrapped to [0, 360), s/l 0–100). */
function hslToRgb(h: number, s: number, l: number): Rgb {
  const sat = s / 100
  const light = l / 100
  const c = (1 - Math.abs(2 * light - 1)) * sat
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = light - c / 2
  let r1 = 0, g1 = 0, b1 = 0
  if (h < 60) { r1 = c; g1 = x; b1 = 0 }
  else if (h < 120) { r1 = x; g1 = c; b1 = 0 }
  else if (h < 180) { r1 = 0; g1 = c; b1 = x }
  else if (h < 240) { r1 = 0; g1 = x; b1 = c }
  else if (h < 300) { r1 = x; g1 = 0; b1 = c }
  else { r1 = c; g1 = 0; b1 = x }
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }
}

export function parseColor(css: string): Rgb | null {
  if (typeof css !== 'string') return null
  let s = css.trim().toLowerCase()
  if (!s) return null
  if (NAMED[s]) s = NAMED[s]

  const hex = HEX.exec(s)
  if (hex) {
    const h = hex[1]
    // 3 and 4 digit forms double each nibble; 8 drops the alpha pair.
    if (h.length === 3 || h.length === 4) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16),
      }
    }
    if (h.length === 6 || h.length === 8) {
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
      }
    }
    return null
  }

  const fn = RGB_FN.exec(s)
  if (fn) {
    const nums = [fn[1], fn[2], fn[3]].map(Number)
    if (nums.some((n) => !Number.isFinite(n) || n < 0 || n > 255)) return null
    return { r: Math.round(nums[0]), g: Math.round(nums[1]), b: Math.round(nums[2]) }
  }

  const hslMatch = HSL_FN.exec(s)
  if (hslMatch) {
    // Alpha is discarded, never folded into the channels — same rule as the
    // 8-digit hex and rgba() branches above. It rides either the modern
    // `/ a` suffix (`hsl(340 60% 85% / 0.5)`) or, in the legacy comma
    // syntax, a 4th comma-separated value (`hsla(340, 60%, 85%, 0.5)`); the
    // slash form is stripped first so a mixed/malformed remainder can't
    // masquerade as a legitimate 4th comma part.
    const slashIndex = hslMatch[1].indexOf('/')
    const inner = (slashIndex === -1 ? hslMatch[1] : hslMatch[1].slice(0, slashIndex)).trim()

    let parts: string[]
    if (inner.includes(',')) {
      const commaParts = inner.split(',').map((p) => p.trim()).filter(Boolean)
      if (commaParts.length !== 3 && commaParts.length !== 4) return null
      parts = commaParts.slice(0, 3)
    } else {
      parts = inner.split(/\s+/).filter(Boolean)
      if (parts.length !== 3) return null
    }

    const hue = parseHue(parts[0])
    const sat = parsePercent(parts[1])
    const light = parsePercent(parts[2])
    if (hue === null || sat === null || light === null) return null
    return hslToRgb(hue, sat, light)
  }

  return null
}

function channel(v: number): number {
  const s = v / 255
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(c: Rgb): number {
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b)
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const hi = Math.max(la, lb)
  const lo = Math.min(la, lb)
  return (hi + 0.05) / (lo + 0.05)
}

const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const WHITE: Rgb = { r: 255, g: 255, b: 255 }

/** Black or white, whichever reads better on `background`. This is what
 *  replaces the four hard-coded `#fff` rules that sit on the primary. */
export function onColor(background: Rgb): '#000000' | '#ffffff' {
  return contrastRatio(BLACK, background) >= contrastRatio(WHITE, background)
    ? '#000000'
    : '#ffffff'
}

export function meetsAA(fg: Rgb, bg: Rgb, large = false): boolean {
  return contrastRatio(fg, bg) >= (large ? 3 : 4.5)
}
